import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import i18n from 'i18next';

import { AuthContext } from './context';
import { InitialAuthState } from '@global/hooks/useAuth/const';
import { Spinner } from '@global/components/UI Elements/Spinner';

// Services
import { introspectToken } from '@global/hooks/useAuth/requests';
import {
  clearTokensFromCookies,
  customParseJwt,
  getUserFormToken,
  VIAX_KEY,
} from './utils';
import { prefixPath } from '@global/localization/services';
import { getTokensFromCookies } from '@global/hooks/useAuth/utils';

// Interfaces and constants
import * as Constants from '@global/App/constants/constants';
import { User } from '@global/types/types';

import { setCartCookieExpTime, setCookieItem } from '@global/utils/cookie';
import { SiteConfig } from '@global/utils/SiteConfig';
import { shouldTriggerConnectAuth, useOpenIdConnect } from './openIdConnect';

type fromLocationState = {
  from?: {
    pathname: string;
  };
};

function useProvideAuth(): typeof InitialAuthState {
  const history = useHistory();
  const location = useLocation();

  const [user, setUser] = useState<User>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const { isOidcInitialized, getConnectTokens, redirectToConnectForAuth } =
    useOpenIdConnect();

  const validateAllowedRoute = useCallback(() => {
    const checkoutRoute = prefixPath(Constants.Pages.CHECKOUT, i18n.language);
    const confirmationRoute = prefixPath(
      Constants.Pages.ORDER_CONFIRMATION,
      i18n.language
    );
    const allowedRoutes = [checkoutRoute, confirmationRoute];
    const isAllowedRoute = allowedRoutes.some((allowedRoute) =>
      allowedRoute.includes(location.pathname)
    );

    return isAllowedRoute;
  }, [location.pathname]);

  const redirectAfterLogin = useCallback(() => {
    const isAllowedRoute = validateAllowedRoute();
    if (isAllowedRoute) return;

    const originState: fromLocationState = location?.state;
    const { isB2CSite } = SiteConfig.getConfig();
    const fallbackRedirect = isB2CSite
      ? Constants.CUSTOMER_PORTAL_URL
      : prefixPath(Constants.Pages.SUBSCRIPTION_DETAILS, i18n.language);
    const redirectTo = originState?.from?.pathname || fallbackRedirect;
    history.push(redirectTo + location.search);
  }, [location]);

  const redirectToAuth = useCallback(async () => {
    const shouldRedirect = await redirectToConnectForAuth();
    if (shouldRedirect) return;

    let redirectPath = Constants.REGISTER_PATH;
    const currentPage = window.location.href;
    const params = { target: currentPage };

    if (currentPage.includes(Constants.Pages.SUBSCRIPTION_DETAILS)) {
      params['view'] = 'minimal';
      redirectPath = Constants.LOGIN_PATH;
    }

    window.location.href = SiteConfig.buildRedirectUrl(
      Constants.CART_CHECKOUT_BASE_URL,
      redirectPath,
      params
    );
  }, [redirectToConnectForAuth]);

  const doLogout = useCallback(() => {
    clearTokensFromCookies();
    setUser(null);
    redirectToAuth();
  }, [redirectToAuth]);

  const updateAuthState = useCallback(async () => {
    const user = getUserFormToken();
    if (user) {
      setUser(user);
      redirectAfterLogin();
    } else {
      redirectToAuth();
    }
  }, [redirectToAuth, redirectAfterLogin]);

  const checkAuth = useCallback(async () => {
    const { almToken, viaxToken, connectAccessToken, connectIdToken } =
      getTokensFromCookies();

    const { tokenIsValid, ecomToken } = await introspectToken(
      almToken || connectAccessToken,
      viaxToken,
      connectIdToken
    );

    if (!tokenIsValid) {
      clearTokensFromCookies();
      redirectToAuth();
      return;
    }

    const oneHourFromNow = Date.now() / 1000 + 3600;
    const exp = almToken ? customParseJwt(almToken).exp : oneHourFromNow;

    setCookieItem(VIAX_KEY, ecomToken, exp);

    const { siteId, locale } = SiteConfig.getConfig();
    setCartCookieExpTime(locale, siteId, exp);
  }, [redirectToAuth]);

  const init = useCallback(async () => {
    setIsLoading(true);
    await getConnectTokens();
    await checkAuth();
    await updateAuthState();
    setIsLoading(false);
  }, [getConnectTokens, checkAuth, updateAuthState]);

  useEffect(() => {
    const shouldTriggerConnect = shouldTriggerConnectAuth();
    if (!shouldTriggerConnect || (shouldTriggerConnect && isOidcInitialized)) {
      init();
    }
  }, [isOidcInitialized]);

  return {
    user,
    isLoggedIn: Boolean(user),
    isLoading,
    doLogout,
  };
}

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: FC = ({ children }) => {
  const value = useProvideAuth();

  return (
    <AuthContext.Provider value={value}>
      {value.isLoading ? (
        <Spinner withOverlay fullScreen size="64px" />
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
